import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Chip,
  Typography,
  Tooltip,
  Badge,
  Avatar,
  AvatarGroup,
  Popover,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Paper,
  useTheme,
  alpha
} from '@mui/material';
import {
  Circle as OnlineIcon,
  People as PeopleIcon,
  Group as GroupIcon
} from '@mui/icons-material';
import { presenceService, UserPresence } from '../services/presenceService';

interface OnlineUsersIndicatorProps {
  type: 'community' | 'group';
  groupId?: string;
  groupName?: string;
  showDetails?: boolean;
  variant?: 'chip' | 'badge' | 'full';
}

export const OnlineUsersIndicator: React.FC<OnlineUsersIndicatorProps> = ({
  type,
  groupId,
  groupName,
  showDetails = true,
  variant = 'chip'
}) => {
  const theme = useTheme();
  const [onlineUsers, setOnlineUsers] = useState<UserPresence[]>([]);
  const [loading, setLoading] = useState(true);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const onlineCount = onlineUsers.length;
  const displayUsers = onlineUsers.slice(0, 3);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (showDetails && onlineCount > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    let unsubscribe: (() => void) | null = null;

    const setupListener = () => {
      // Direct callback without debouncing for real-time updates
      const updateCallback = (users: UserPresence[]) => {
        setOnlineUsers(users);
        setLoading(false);
      };

      if (type === 'community') {
        unsubscribe = presenceService.onCommunityOnlineUsersChange(updateCallback);
      } else if (type === 'group' && groupId) {
        unsubscribe = presenceService.onGroupOnlineUsersChange(groupId, updateCallback);
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [type, groupId]);

  // Performance: Memoize the chip variant render
  const renderChipVariant = useMemo(() => (
    <Chip
      icon={<OnlineIcon sx={{ color: theme.palette.success.main, fontSize: 12 }} />}
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography variant="caption" sx={{ fontWeight: 500 }}>
            {onlineCount}
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.8 }}>
            online
          </Typography>
        </Box>
      }
      size="small"
      onClick={handleClick}
      sx={{
        bgcolor: alpha(theme.palette.success.main, 0.1),
        color: theme.palette.success.main,
        cursor: showDetails && onlineCount > 0 ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: showDetails && onlineCount > 0 ? alpha(theme.palette.success.main, 0.2) : undefined,
        },
      }}
    />
  ), [theme, onlineCount, handleClick, showDetails]);

  // Performance: Memoize the badge variant render
  const renderBadgeVariant = useMemo(() => (
    <Badge
      badgeContent={onlineCount}
      color="success"
      showZero={false}
      onClick={handleClick}
      sx={{
        cursor: showDetails && onlineCount > 0 ? 'pointer' : 'default',
      }}
    >
      {type === 'community' ? <PeopleIcon /> : <GroupIcon />}
    </Badge>
  ), [onlineCount, handleClick, showDetails, type]);

  // Performance: Memoize the full variant render
  const renderFullVariant = useMemo(() => (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: 1,
        borderRadius: 1,
        bgcolor: alpha(theme.palette.success.main, 0.1),
        cursor: showDetails && onlineCount > 0 ? 'pointer' : 'default',
        '&:hover': {
          bgcolor: showDetails && onlineCount > 0 ? alpha(theme.palette.success.main, 0.2) : undefined,
        },
      }}
    >
      <OnlineIcon sx={{ color: theme.palette.success.main, fontSize: 16 }} />
      <Typography variant="body2" sx={{ color: theme.palette.success.main, fontWeight: 500 }}>
        {onlineCount} online
        {type === 'group' && groupName && (
          <Typography component="span" variant="caption" sx={{ ml: 0.5, opacity: 0.8 }}>
            in {groupName}
          </Typography>
        )}
      </Typography>
      {onlineCount > 0 && (
        <AvatarGroup max={3} sx={{ ml: 1 }}>
          {displayUsers.map((user) => (
            <Avatar
              key={user.userId}
              sx={{
                width: 24,
                height: 24,
                fontSize: '0.7rem',
                bgcolor: theme.palette.primary.main,
              }}
            >
              {user.displayName.charAt(0).toUpperCase()}
            </Avatar>
          ))}
        </AvatarGroup>
      )}
    </Box>
  ), [theme, onlineCount, handleClick, showDetails, type, groupName, displayUsers]);

  // Performance: Memoize the users list render
  const renderUsersList = useMemo(() => (
    <Popover
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <Paper sx={{ maxHeight: 300, width: 250, overflow: 'auto' }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {type === 'community' ? 'Online in Community' : `Online in ${groupName || 'Group'}`}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {onlineCount} user{onlineCount !== 1 ? 's' : ''} online
          </Typography>
        </Box>
        <List dense>
          {onlineUsers.map((user) => (
            <ListItem key={user.userId}>
              <ListItemAvatar>
                <Badge
                  badgeContent={<OnlineIcon sx={{ fontSize: 8 }} />}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                  }}
                  sx={{
                    '& .MuiBadge-badge': {
                      bgcolor: theme.palette.success.main,
                      color: theme.palette.success.main,
                      p: 0,
                      minWidth: 12,
                      height: 12,
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      bgcolor: theme.palette.primary.main,
                      fontSize: '0.8rem',
                    }}
                  >
                    {user.displayName.charAt(0).toUpperCase()}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={user.displayName}
                secondary={
                  <Typography variant="caption" color="success.main">
                    Online now
                  </Typography>
                }
              />
            </ListItem>
          ))}
        </List>
      </Paper>
    </Popover>
  ), [anchorEl, handleClose, type, groupName, onlineCount, onlineUsers, theme]);

  // Performance: Early return for loading state
  if (loading) {
    return (
      <Chip
        label="Loading..."
        size="small"
        sx={{ opacity: 0.6 }}
      />
    );
  }

  return (
    <>
      {variant === 'chip' && renderChipVariant}
      {variant === 'badge' && renderBadgeVariant}
      {variant === 'full' && renderFullVariant}
      {showDetails && renderUsersList}
    </>
  );
};

// Performance: Add display name for debugging
OnlineUsersIndicator.displayName = 'OnlineUsersIndicator';

export default OnlineUsersIndicator; 